import { DINDIN_API_BASE_URL } from './config';
import { loadingStore } from '../../loading/loadingStore';

function resolveApiToken() {
  const t = process.env.REACT_APP_DINDIN_API_TOKEN;
  return t && String(t).trim() ? String(t).trim() : '';
}

function withApiToken(headers) {
  const token = resolveApiToken();
  if (!token) return headers;

  // Do not overwrite if caller already provided it (case-insensitive).
  const existingKeys = Object.keys(headers || {});
  const hasHeader = existingKeys.some((k) => k.toLowerCase() === 'x-api-token');
  if (hasHeader) return headers;

  return { ...(headers || {}), 'x-api-token': token };
}

/**
 * Builds an absolute URL against the configured API base (handles trailing slashes).
 */
export function apiUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  const base = DINDIN_API_BASE_URL.endsWith('/')
    ? DINDIN_API_BASE_URL.slice(0, -1)
    : DINDIN_API_BASE_URL;

  // Guard against accidental double segments when env base already includes them
  // (e.g. base = ".../api/v1/dindin-web" while path starts with "/dindin-web/...").
  if (base.endsWith('/dindin-web') && p.startsWith('/dindin-web/')) {
    return `${base}${p.slice('/dindin-web'.length)}`;
  }

  return `${base}${p}`;
}

/**
 * GET JSON from the DinDin API.
 * Throws on non-OK HTTP status or invalid JSON.
 *
 * @param {string} path - Path relative to API base, e.g. `/getCuisines`
 * @param {RequestInit} [init] - Optional fetch init (headers, signal, etc.)
 * @returns {Promise<unknown>}
 */
export async function getJson(path, init = {}) {
  const { globalLoading = true, ...fetchInit } = init || {};
  const url = apiUrl(path);
  if (globalLoading) loadingStore.begin();
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...withApiToken(fetchInit.headers || {}),
      },
      ...fetchInit,
    });
  } finally {
    if (globalLoading) loadingStore.end();
  }

  const text = await res.text();
  let body;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Invalid JSON from ${url}`);
  }

  if (!res.ok) {
    const msg =
      (body && typeof body === 'object' && body.message) ||
      `Request failed: ${res.status} ${res.statusText}`;
    throw new Error(String(msg));
  }

  return body;
}

async function requestJson(method, path, { body, token, globalLoading = true, ...init } = {}) {
  const url = apiUrl(path);
  if (globalLoading) loadingStore.begin();
  let res;
  try {
    res = await fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : null),
        ...(token ? { Authorization: `Bearer ${token}` } : null),
        ...withApiToken(init.headers || {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : null),
      ...init,
    });
  } finally {
    if (globalLoading) loadingStore.end();
  }

  const text = await res.text();
  let json;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Invalid JSON from ${url}`);
  }

  if (!res.ok) {
    const msg =
      (json && typeof json === 'object' && json.message) ||
      `Request failed: ${res.status} ${res.statusText}`;
    throw new Error(String(msg));
  }

  return json;
}

/**
 * POST JSON to the DinDin API.
 *
 * @param {string} path
 * @param {{ body?: any, token?: string } & RequestInit} [init]
 * @returns {Promise<unknown>}
 */
export async function postJson(path, init = {}) {
  return requestJson('POST', path, init);
}

/**
 * PUT JSON to the DinDin API.
 *
 * @param {string} path
 * @param {{ body?: any, token?: string } & RequestInit} [init]
 * @returns {Promise<unknown>}
 */
export async function putJson(path, init = {}) {
  return requestJson('PUT', path, init);
}

/**
 * PATCH JSON to the DinDin API.
 *
 * @param {string} path
 * @param {{ body?: any, token?: string } & RequestInit} [init]
 * @returns {Promise<unknown>}
 */
export async function patchJson(path, init = {}) {
  return requestJson('PATCH', path, init);
}

/**
 * DELETE JSON from the DinDin API (with optional JSON body).
 *
 * @param {string} path
 * @param {{ body?: any, token?: string } & RequestInit} [init]
 * @returns {Promise<unknown>}
 */
export async function deleteJson(path, init = {}) {
  return requestJson('DELETE', path, init);
}
